// Karten-Layout: SPALTEN-STAPEL (portiert aus home-dash, Konzept V2
// von Tim, 2026-07-10).
//
//   - 4 Spalten, jede ist ein geordneter Stapel von Karten (wie Trello).
//     Ziehen = Spalte + Einfuegeposition. Keine freien Zellen, keine
//     Luecken, kein Aufraeumen noetig.
//   - Die HOEHE einer Karte ist IMMER ihr Inhalt (live gemessen, auf
//     Rasterzellen gerundet) — nichts wird abgeschnitten. Waechst der
//     Inhalt, ruecken die Karten darunter in ihrer Spalte nach unten.
//   - Einzige Groessen-Wahl pro Karte: BREITE 1 oder 2 Spalten. Der
//     "sinnvolle Rahmen" kommt vom Aufrufer: CardGrid laesst den
//     Breite-Toggle nur zu, wo allowWide(id) true liefert.
//
// Das Packen laeuft als Skyline in Wellen: es platziert immer die
// Spalten-Kopf-Karte mit der kleinsten Zielzeile — so stapeln schmale
// Karten neben einer breiten weiter oben, statt stumpf unter ihr.
// Persistenz ueber den prefs-Store (Server-SSOT + WS-Sync zwischen
// Geraeten, siehe prefs.svelte.ts) unter `card_layout_<group>`.

import { getPref, setPref } from "./prefs.svelte";

export const GRID_COLS = 4;

// Listen-Modell: je Spalte die Karten-IDs in Stapel-Reihenfolge; wide
// markiert 2-spaltige Karten (Anker = eigene Spalte, geclamped); rows
// sind manuelle Hoehen-Overrides in Rasterzeilen (Minimum bleibt immer
// die Inhaltshoehe -- der Override kann eine Karte nur VERGROESSERN).
export interface CardLists {
  cols: string[][];
  wide: Record<string, boolean>;
  rows: Record<string, number>;
  /** Karten, die der Nutzer entfernt hat. Sie bleiben bekannt (und im
   *  "Karte hinzufuegen"-Menue), liegen aber nicht im Raster. */
  hidden: string[];
}

// Ergebnis des Packens: konkrete Zellen je Karte.
export interface Placed {
  c: number; // Anker-Spalte
  r: number; // Zeile
  w: number; // Spalten-Spannweite (1|2)
  rows: number; // Zeilen-Spannweite (aus Inhaltshoehe)
}

const KEY_PREFIX = "card_layout_";

function key(group: string): string {
  return `${KEY_PREFIX}${group}`;
}

interface StoredV3 {
  v: 3;
  cols: string[][];
  wide: Record<string, boolean>;
  rows?: Record<string, number>;
  /** seit 19.09.2026; fehlt in aelteren Staenden = nichts entfernt */
  hidden?: string[];
}

function loadSaved(group: string): StoredV3 | null {
  const raw = getPref<Record<string, unknown>>(key(group));
  if (!raw || typeof raw !== "object") return null;
  if (raw.v === 3 && Array.isArray(raw.cols)) return raw as unknown as StoredV3;
  return null;
}

// resolveLists liefert einen VOLLSTAENDIGEN Stand fuer die aktuellen
// Karten-IDs: gespeicherte Reihenfolge bleibt, verschwundene IDs fallen
// weg, neue Karten landen unten in der kuerzesten Spalte. defaultWide
// greift nur fuer Karten ohne gespeicherten Stand (Erstanlage/neue ID).
// Reaktiv, weil getPref den prefs-Store liest.
export function resolveLists(group: string, ids: string[], defaultWide: string[] = []): CardLists {
  const saved = loadSaved(group);
  const want = new Set(ids);
  const cols: string[][] = Array.from({ length: GRID_COLS }, () => []);
  const wide: Record<string, boolean> = {};
  const rows: Record<string, number> = {};
  const hidden = (saved?.hidden ?? []).filter((id) => want.has(id));
  const versteckt = new Set(hidden);
  const seen = new Set<string>();
  if (saved) {
    for (let c = 0; c < GRID_COLS; c++) {
      for (const id of saved.cols[c] ?? []) {
        if (!want.has(id) || seen.has(id) || versteckt.has(id)) continue;
        cols[c].push(id);
        seen.add(id);
        if (saved.wide[id]) wide[id] = true;
        const r = saved.rows?.[id];
        if (typeof r === "number" && r > 0) rows[id] = r;
      }
    }
  }
  for (const id of ids) {
    if (seen.has(id) || versteckt.has(id)) continue;
    let best = 0;
    for (let c = 1; c < GRID_COLS; c++) {
      if (cols[c].length < cols[best].length) best = c;
    }
    cols[best].push(id);
    if (defaultWide.includes(id)) wide[id] = true;
  }
  return { cols, wide, rows, hidden };
}

export function saveLists(group: string, lists: CardLists): void {
  setPref(key(group), {
    v: 3,
    cols: lists.cols,
    wide: lists.wide,
    rows: lists.rows,
    hidden: lists.hidden,
  } satisfies StoredV3);
}

// resetLayout verwirft den gespeicherten Stand der Gruppe -- naechstes
// resolveLists liefert wieder die Server-Default-Verteilung.
export function resetLayout(group: string): void {
  setPref(key(group), null);
}

// insertAt bewegt eine Karte an (Spalte, Index) — Einfuege-Modell.
export function insertAt(lists: CardLists, id: string, col: number, index: number): CardLists {
  const cols = lists.cols.map((list) => list.filter((x) => x !== id));
  const c = Math.max(0, Math.min(GRID_COLS - 1, col));
  const i = Math.max(0, Math.min(cols[c].length, index));
  cols[c] = [...cols[c].slice(0, i), id, ...cols[c].slice(i)];
  return { cols, wide: lists.wide, rows: lists.rows, hidden: lists.hidden };
}

export function toggleWide(lists: CardLists, id: string): CardLists {
  return {
    cols: lists.cols,
    wide: { ...lists.wide, [id]: !lists.wide[id] },
    rows: lists.rows,
    hidden: lists.hidden,
  };
}

// setRows setzt/loescht den manuellen Hoehen-Override einer Karte
// (null = zurueck auf Inhaltshoehe).
export function setRows(lists: CardLists, id: string, rows: number | null): CardLists {
  const next = { ...lists.rows };
  if (rows === null) delete next[id];
  else next[id] = rows;
  return { cols: lists.cols, wide: lists.wide, rows: next, hidden: lists.hidden };
}

// packLayout: Skyline-Packing in Wellen. rowsOfCard liefert die aktuelle
// Inhaltshoehe in Zellen (gemessen; Fallback fuer noch Ungemessenes beim
// Aufrufer).
export function packLayout(lists: CardLists, rowsOfCard: (id: string) => number): Record<string, Placed> {
  const skyline = Array<number>(GRID_COLS).fill(0);
  const heads = Array<number>(GRID_COLS).fill(0);
  const out: Record<string, Placed> = {};
  for (;;) {
    let pick = -1;
    let pickR = Infinity;
    let pickAnchor = 0;
    for (let c = 0; c < GRID_COLS; c++) {
      const id = lists.cols[c][heads[c]];
      if (!id) continue;
      const w = lists.wide[id] ? 2 : 1;
      const anchor = Math.min(c, GRID_COLS - w);
      let r = 0;
      for (let k = 0; k < w; k++) r = Math.max(r, skyline[anchor + k]);
      if (r < pickR) {
        pick = c;
        pickR = r;
        pickAnchor = anchor;
      }
    }
    if (pick < 0) return out;
    const id = lists.cols[pick][heads[pick]];
    const w = lists.wide[id] ? 2 : 1;
    const rows = Math.max(1, rowsOfCard(id));
    out[id] = { c: pickAnchor, r: pickR, w, rows };
    for (let k = 0; k < w; k++) skyline[pickAnchor + k] = pickR + rows;
    heads[pick]++;
  }
}

// entferneKarte nimmt eine Karte aus dem Raster -- sie bleibt bekannt
// und kommt ueber "Karte hinzufuegen" zurueck. Bewusst kein Loeschen:
// eine Karte, die weg IST, koennte man nicht wiederfinden.
export function entferneKarte(lists: CardLists, id: string): CardLists {
  return {
    cols: lists.cols.map((list) => list.filter((x) => x !== id)),
    wide: lists.wide,
    rows: lists.rows,
    hidden: lists.hidden.includes(id) ? lists.hidden : [...lists.hidden, id],
  };
}

// zeigeKarte stellt eine entfernte Karte zurueck ins Raster: unten in
// die kuerzeste Spalte, da faellt sie am wenigsten ins Gewicht.
export function zeigeKarte(lists: CardLists, id: string): CardLists {
  const cols = lists.cols.map((list) => list.filter((x) => x !== id));
  let best = 0;
  for (let c = 1; c < GRID_COLS; c++) if (cols[c].length < cols[best].length) best = c;
  cols[best].push(id);
  return { cols, wide: lists.wide, rows: lists.rows, hidden: lists.hidden.filter((x) => x !== id) };
}
